import { Row, StatColumn, StatProps, TextVariant, Text, TooltipText } from "@bridgesplit/ui";
import { bsMath, formatPercent, formatUsd } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { convertPrincipalToLpTokenAmount, useVaultRestrictedWithdrawAmount } from "@bridgesplit/abf-react";

import { useVaultContext } from "../VaultContext";
import { useVaultWithdrawContext } from "./VaultWithdrawContext";

const variant: TextVariant = "body1";
export default function VaultWithdrawStats() {
    const { form } = useVaultWithdrawContext();
    const { vaultExpanded, userPosition } = useVaultContext();
    const { principalRestrictedWithdrawAmount } = useVaultRestrictedWithdrawAmount({ vaultExpanded, userPosition });
    const lpValue = convertPrincipalToLpTokenAmount(form.amountPrincipal, vaultExpanded);
    const postWithdrawUsd = useGetPostWithdrawUsd();

    if (!userPosition) return null;

    const currentWithdrawalUtilization = bsMath.div(
        userPosition?.amountWithdrawnInCurrentWindow,
        vaultExpanded?.vaultMetadata?.withdrawalLimit
    );

    const postWithdrawalUtilization = bsMath.div(
        bsMath.add(lpValue, userPosition?.amountWithdrawnInCurrentWindow),
        vaultExpanded?.vaultMetadata?.withdrawalLimit
    );

    const hideWithdrawUtilization =
        !principalRestrictedWithdrawAmount || (!currentWithdrawalUtilization && !postWithdrawalUtilization);

    const stats: StatProps[] = [
        {
            caption: (
                <Row spacing={0.5}>
                    <Text variant="body1" color="caption">
                        Withdraw Utilization
                    </Text>
                    <TooltipText
                        tooltipProps={{ placement: "top" }}
                        helpText={`Vault currently has a 24 hour withdraw cap`}
                        color="caption"
                    />
                </Row>
            ),
            value: !form.amountPrincipal
                ? formatPercent(currentWithdrawalUtilization)
                : [formatPercent(currentWithdrawalUtilization), formatPercent(postWithdrawalUtilization)],
            hide: hideWithdrawUtilization
        },
        {
            caption: COPY.USER_POSITION_TERM,
            value: [formatUsd(userPosition.amountAvailable.amountUsd), formatUsd(postWithdrawUsd)],
            hide: !form.amountPrincipal
        }
    ];

    return <StatColumn variant={variant} stats={stats} />;
}

/**
 * Lp amount is typically rising constantly. So when you click max withdraw we don't want to userPosition.amountAvailable.amountUsd - usdValue
 * because after a few seconds the after balance will grow to pennies, which creates a bad UX.
 *
 * So if isMaxWithdraw is set, and the amount isn't restricted by a vault limit, then just show 0,
 * because the BE is going to zero out the account.
 */
function useGetPostWithdrawUsd() {
    const { form } = useVaultWithdrawContext();
    const { vaultExpanded, userPosition } = useVaultContext();
    const { principalRestrictedWithdrawAmount } = useVaultRestrictedWithdrawAmount({ vaultExpanded, userPosition });

    if (!vaultExpanded || !userPosition) return undefined;

    const usdValue = vaultExpanded?.principalOracle.getUsdAmount(form.amountPrincipal);

    return form.isMaxWithdraw && !principalRestrictedWithdrawAmount
        ? 0
        : Math.max(0, userPosition.amountAvailable.amountUsd - (usdValue ?? 0));
}
